import { ClockCircleOutlined } from "@ant-design/icons";
import { Button, Form, Input, message, Radio } from "antd";
import { SwapBoxLineIcon } from "components/svg-icons";
import AutodeskWelcomeLogoIcon from "components/svg-icons/autodesk-welcome-logo-icon";
import ProcoreLogoIcon from "components/svg-icons/procore-logo-icon";
import RedirectIcon from "components/svg-icons/redirect-icon";
import { IntegrationAuthProcessStatuses } from "constants/index";
import { ProjectContext } from "context/ProjectProvider";
import { useIntegrationAuthProcess } from "hooks/integration-hooks";
import { ProjectIntegrationsTabSharedContext } from "pages/project-setting-menu/project-integrations/project-integrations-tab-context";
import { IntegrationType } from "pages/subscription-settings/integrations-tab-new/utils";
import { useContext, useState } from "react";
import { getOAuthUrl } from "services/integration-service";

export default function IntegrationStepForm({
  setButtonEnable
}: {
  setButtonEnable: React.Dispatch<React.SetStateAction<boolean>>;
}) {
  const [integrationType, setIntegrationType] = useState(
    IntegrationType.AUTODESK
  );
  const { tokenRetrievalState, gqlClientForProject } =
    useContext(ProjectContext);

  const { dispatch } = useContext(ProjectIntegrationsTabSharedContext);

  const { initAuthProcess } = useIntegrationAuthProcess(
    "project",
    gqlClientForProject
  );

  const [isLoginSeparateWindowOpened, setSeparateWindowOpened] =
    useState(false);

  const onIntegration = async (values: any) => {
    if (integrationType === IntegrationType.AUTODESK) {
      setSeparateWindowOpened(true);
      const OAuthURLResponse = await getOAuthUrl(
        IntegrationType[IntegrationType.AUTODESK],
        {},
        tokenRetrievalState?.token
      );
      if (OAuthURLResponse.success) {
        const authResponse: any = await initAuthProcess(OAuthURLResponse.data);
        if (
          authResponse?.oauth_status?.status ===
          IntegrationAuthProcessStatuses.PROCESSED
        ) {
          dispatch({
            type: "SET_SHARED_STATE",
            payload: {
              activeView: "configure-initial",
              system: authResponse.source_type.toUpperCase(),
              additionalData: {
                newIntegrationName: values.name
              }
            }
          });
          setButtonEnable(true);
        } else {
          const error = authResponse.output?.error || "Authorization failed";
          message.error(error);
        }
      } else {
        setSeparateWindowOpened(false);
      }
    }

    if (integrationType === IntegrationType.PROCORE) {
      dispatch({
        type: "SET_SHARED_STATE",
        payload: {
          activeView: "configure-initial",
          system: IntegrationType[IntegrationType.PROCORE],
          additionalData: {
            newIntegrationName: values.name
          }
        }
      });
      setButtonEnable(true);
    }
  };

  const firstFormDiv = (
    <Form
      className="h-full w-[440px] flex flex-col py-10 space-y-5 welcome-startup-setup"
      onFinish={onIntegration}
      layout="vertical"
      initialValues={{ integrationType: IntegrationType.AUTODESK, name: "" }}
    >
      <Form.Item name="integrationType" label="System">
        <Radio.Group
          value={integrationType}
          defaultValue={integrationType}
          onChange={(env) => {
            setIntegrationType(env.target.value as IntegrationType);
          }}
          className="flex space-x-3 "
        >
          <Radio.Button value={IntegrationType.AUTODESK}>
            <div className="w-52 flex flex-col items-center">
              <AutodeskWelcomeLogoIcon />
              <div>Autodesk Forma</div>
            </div>
          </Radio.Button>
          <Radio.Button value={IntegrationType.PROCORE}>
            <div className="flex flex-col w-52 items-center">
              <ProcoreLogoIcon />
              <div>Procore</div>
            </div>
          </Radio.Button>
        </Radio.Group>
      </Form.Item>

      <Form.Item
        name="name"
        label="Name"
        required
        rules={[
          {
            required: true,
            message: "Please enter the Name for the integration."
          }
        ]}
      >
        <Input
          placeholder="Add a name for the integration"
          className="rounded-lg"
        />
      </Form.Item>

      <div className="flex w-full p-2 bg-ciq-gray-50 border border-solid border-white rounded-lg">
        <div className="px-2">
          <SwapBoxLineIcon />
        </div>
        <div className="flex-col opacity-90">
          <div>Information Synchronized:</div>
          <ul className="list-disc">
            <li>Submittal Log</li>
            <li>Construction Documents</li>
            <li>Project Users</li>
            <li>Companies</li>
          </ul>
        </div>
      </div>

      <div className="flex justify-center items-center pt-2">
        <Button
          htmlType="submit"
          className="w-full rounded-lg flex justify-center items-center space-x-1"
        >
          <div>Proceed to Sign In </div>
          <RedirectIcon />
        </Button>
      </div>
    </Form>
  );

  const whenLoginSeparateWindowOpenedDiv = (
    <div className="h-full w-[440px] flex flex-col items-center py-10">
      <div className="flex justify-center items-center space-x-1 p-2">
        <AutodeskWelcomeLogoIcon />
        <div>Autodesk Forma</div>
      </div>
      <div className="flex justify-center items-center space-x-1 border-ciq-gray-300 border border-solid rounded-md  bg-opacity-5 px-2 py-1">
        <ClockCircleOutlined />
        <div>Waiting for Account Information</div>
      </div>

      <div className="py-8 text-center">
        The Autodesk Sign In Page is open in a separate window. Your Account
        details will be available here after signing in.
      </div>
      <div className="h-28 w-10" />
      <div className="text-xs italic py-1">Can’t see the window?</div>
      <Button className="flex justify-center items-center space-x-1 px-8 rounded-md">
        <div>Reopen Window</div> <RedirectIcon />
      </Button>
    </div>
  );

  return isLoginSeparateWindowOpened
    ? whenLoginSeparateWindowOpenedDiv
    : firstFormDiv;
}
